import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { Notification } from '@alliance-disposal/transport-types';
import { formatE164ToUSPhoneNumber } from '@wayste/client-utils';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@wayste/shadcn/components/dropdown-menu';
import { SourTable } from '@wayste/shadcn/components/sour-table/sour-table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@wayste/shadcn/components/tooltip';
import { BadgeCheck, MoreVertical, StickyNote } from 'lucide-react';
import { CopyToClipboard } from '../copy-to-clipboard';
const CELL_PADDING = '!py-4 !px-3';
const NOTIFY_TOTAL = Notification.CommunicationTypes.length;
function countEnabledNotificationTypes(prefs) {
    return Notification.CommunicationTypes.filter((t) => (prefs[t]?.length ?? 0) > 0).length;
}
/** Notify column label, e.g. `5/10`, aligned with `Notification.CommunicationTypes`. */
export function notifyLabelForContact(contact) {
    return `${countEnabledNotificationTypes(contact.notificationPreferences)}/${NOTIFY_TOTAL}`;
}
export function formatContactDisplayName(contact) {
    return ([contact.firstName, contact.lastName]
        .filter((s) => Boolean(s?.trim()))
        .join(' ')
        .trim() || '—');
}
export function sortCustomerContactsPrimaryFirst(contacts) {
    const primaryIndex = contacts.findIndex((c) => c.primaryContact);
    if (primaryIndex < 0)
        return contacts;
    return [contacts[primaryIndex], ...contacts.slice(0, primaryIndex), ...contacts.slice(primaryIndex + 1)];
}
export const CustomerContactsTable = ({ contacts, onEditContact, onRemoveContact, emptyState = _jsx("p", { className: "text-text-secondary py-4 text-center text-sm", children: "No contacts added yet." }), isLoading = false, }) => {
    const columns = useMemo(() => [
        {
            key: 'name',
            label: 'Name',
            cellClassName: CELL_PADDING,
            render: (row) => (_jsx("div", { className: "text-text-primary flex items-center gap-1.5 text-sm font-medium", children: formatContactDisplayName(row) })),
        },
        {
            key: 'email',
            label: 'Email',
            cellClassName: CELL_PADDING,
            render: (row) => (_jsxs("div", { className: "group flex items-center", children: [_jsx("span", { className: "text-primary-on-surface-text text-sm", children: row.email ?? '—' }), row.email ? _jsx(CopyToClipboard, { text: row.email }) : null] })),
        },
        {
            key: 'phoneNumber',
            label: 'Phone',
            cellClassName: CELL_PADDING,
            render: (row) => (_jsx("span", { className: "text-text-primary text-sm", children: row.phoneNumber ? formatE164ToUSPhoneNumber(row.phoneNumber) : '—' })),
        },
        {
            key: 'notify',
            label: 'Notify',
            cellClassName: CELL_PADDING,
            render: (row) => notifyLabelForContact(row),
        },
        {
            key: 'primaryContact',
            label: 'Primary',
            cellClassName: CELL_PADDING,
            render: (row) => row.primaryContact ? (_jsx(BadgeCheck, { className: "text-success size-4" })) : (_jsx("span", { className: "text-text-secondary text-sm", children: "\u2014" })),
        },
        {
            key: 'notes',
            label: 'Notes',
            cellClassName: CELL_PADDING,
            render: (row) => {
                if (!row.notes?.length)
                    return _jsx("span", { className: "text-text-primary text-sm", children: "\u2014" });
                return (_jsxs(Tooltip, { children: [_jsx(TooltipTrigger, { asChild: true, children: _jsx(StickyNote, { className: "text-text-secondary size-4 cursor-pointer" }) }), _jsx(TooltipContent, { side: "top", children: row.notes })] }));
            },
        },
        {
            key: 'actions',
            label: '',
            headerRender: () => null,
            cellClassName: CELL_PADDING,
            width: 48,
            render: (row) => (_jsxs(DropdownMenu, { children: [_jsx(DropdownMenuTrigger, { className: "text-text-secondary hover:bg-secondary-hover rounded-radius-sm flex size-8 items-center justify-center p-2 transition-colors", onClick: (e) => e.stopPropagation(), children: _jsx(MoreVertical, { className: "size-4" }) }), _jsxs(DropdownMenuContent, { align: "end", className: "w-32", children: [_jsx(DropdownMenuItem, { onClick: () => onEditContact(row), children: "Edit" }), onRemoveContact ? (_jsx(DropdownMenuItem, { variant: "destructive", onClick: () => onRemoveContact(row.id), children: "Remove" })) : null] })] })),
        },
    ], [onEditContact, onRemoveContact]);
    return _jsx(SourTable, { columns: columns, data: contacts, isLoading: isLoading, emptyState: emptyState });
};
//# sourceMappingURL=customer-contacts-table.js.map